import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  GitBranch,
  Layers,
  Shield,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';

// Hooks & components
import { usePrint } from '../hooks/usePrint';
import PrintButton from '../components/PrintButton';

// Registries
import { getAllStages, getCRMStages, getOutcomes, getPreStage } from '../lib/leadPipelineRegistry';
import { getChannelSLAs, getStageSLAs, getEscalationRules, getExceptionCodes, getSLACounts } from '../lib/slaCatalogueRegistry';
import { getAllRoles, getRoleGroups, getRoleCounts } from '../lib/rolesRegistry';
import { getSOPCounts, getSOPsByPriority } from '../lib/sopsRegistry';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  labelAr: string;
  value: string | number;
  sub?: string;
  subAr?: string;
  color: 'blue' | 'emerald' | 'violet' | 'amber' | 'rose' | 'cyan';
  href?: string;
}

const colorMap = {
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',   icon: 'bg-blue-500',    text: 'text-blue-700',   value: 'text-blue-900' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200',icon: 'bg-emerald-500', text: 'text-emerald-700',value: 'text-emerald-900' },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200', icon: 'bg-violet-500',  text: 'text-violet-700', value: 'text-violet-900' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',  icon: 'bg-amber-500',   text: 'text-amber-700',  value: 'text-amber-900' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',   icon: 'bg-rose-500',    text: 'text-rose-700',   value: 'text-rose-900' },
  cyan:    { bg: 'bg-cyan-50',    border: 'border-cyan-200',   icon: 'bg-cyan-500',    text: 'text-cyan-700',   value: 'text-cyan-900' },
};

const StatCard = ({ icon: Icon, label, labelAr, value, sub, subAr, color, href }: StatCardProps) => {
  const c = colorMap[color];
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const inner = (
    <div className={`rounded-2xl border ${c.bg} ${c.border} p-5 flex items-start gap-4 group transition-all duration-200 hover:shadow-md`}>
      <div className={`${c.icon} rounded-xl p-3 flex-shrink-0`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${c.text} mb-0.5`}>{isAr ? labelAr : label}</p>
        <p className={`text-3xl font-bold ${c.value} leading-none`}>{value}</p>
        {sub && <p className={`text-xs ${c.text} mt-1.5 opacity-80`}>{isAr ? (subAr || sub) : sub}</p>}
      </div>
      {href && <ChevronRight className={`h-4 w-4 ${c.text} opacity-0 group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0`} />}
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
};

interface SectionHeaderProps {
  icon: React.ElementType;
  title: string;
  titleAr: string;
  href?: string;
  linkLabel?: string;
  linkLabelAr?: string;
}

const SectionHeader = ({ icon: Icon, title, titleAr, href, linkLabel, linkLabelAr }: SectionHeaderProps) => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <Icon className="h-5 w-5 text-primary-600" />
        <h2 className="text-lg font-semibold text-gray-900">{isAr ? titleAr : title}</h2>
      </div>
      {href && (
        <Link to={href} className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors">
          {isAr ? (linkLabelAr || 'عرض الكل') : (linkLabel || 'View all')}
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Priority badge
// ─────────────────────────────────────────────────────────────────────────────
const priorityBadge = (p: string) => {
  const map: Record<string, string> = {
    critical: 'bg-rose-100 text-rose-700 border border-rose-200',
    high:     'bg-amber-100 text-amber-700 border border-amber-200',
    medium:   'bg-blue-100 text-blue-700 border border-blue-200',
    low:      'bg-gray-100 text-gray-600 border border-gray-200',
  };
  return map[p] || map.medium;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Dashboard
// ─────────────────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const { printRef, handlePrint } = usePrint('Almajdouie Motors – Lead Management Operating System');

  // ── data ──
  const allStages  = getAllStages();
  const crmStages  = getCRMStages();
  const preStage   = getPreStage();
  const outcomes   = getOutcomes();

  const channelSLAs   = getChannelSLAs();
  const stageSLAs     = getStageSLAs();
  const escalations   = getEscalationRules();
  const exceptions    = getExceptionCodes();
  const slaCounts     = getSLACounts();

  const roles      = getAllRoles();
  const roleGroups = getRoleGroups();
  const roleCounts = getRoleCounts();

  const sopCounts      = getSOPCounts();
  const criticalSOPs   = getSOPsByPriority('critical');

  // ── summary numbers ──
  const summaryCards: StatCardProps[] = [
    {
      icon: GitBranch,
      label: 'Pipeline Stages',
      labelAr: 'مراحل المسار',
      value: allStages.length,
      sub: `${crmStages.length} CRM stages + 1 pre-stage`,
      subAr: `${crmStages.length} مراحل CRM + مرحلة مسبقة`,
      color: 'blue',
      href: '/pipeline',
    },
    {
      icon: Target,
      label: 'Pipeline Outcomes',
      labelAr: 'نتائج المسار',
      value: outcomes.length,
      sub: 'Won, Lost, Nurture, Duplicate, Invalid, Unreachable',
      subAr: 'فوز، خسارة، تغذية، مكرر، غير صالح، لا يمكن الوصول',
      color: 'emerald',
      href: '/pipeline',
    },
    {
      icon: Clock,
      label: 'SLA Definitions',
      labelAr: 'تعريفات مستوى الخدمة',
      value: slaCounts.channelSLAs + slaCounts.stageSLAs,
      sub: `${slaCounts.channelSLAs} channel · ${slaCounts.stageSLAs} stage`,
      subAr: `${slaCounts.channelSLAs} قناة · ${slaCounts.stageSLAs} مرحلة`,
      color: 'amber',
      href: '/sla-catalogue',
    },
    {
      icon: Users,
      label: 'Defined Roles',
      labelAr: 'الأدوار المحددة',
      value: roleCounts.totalRoles,
      sub: `Across ${roleGroups.length} role groups`,
      subAr: `عبر ${roleGroups.length} مجموعات أدوار`,
      color: 'violet',
      href: '/roles',
    },
    {
      icon: BookOpen,
      label: 'Standard Operating Procedures',
      labelAr: 'إجراءات التشغيل الموحدة',
      value: sopCounts.totalSOPs,
      sub: `${sopCounts.stageSOPs} stage · ${sopCounts.scenarioSOPs} scenario`,
      subAr: `${sopCounts.stageSOPs} مرحلة · ${sopCounts.scenarioSOPs} سيناريو`,
      color: 'cyan',
      href: '/sops',
    },
    {
      icon: Shield,
      label: 'Exception Codes',
      labelAr: 'رموز الاستثناء',
      value: exceptions.length,
      sub: `${escalations.length} escalation rules`,
      subAr: `${escalations.length} قواعد التصعيد`,
      color: 'rose',
      href: '/sla-catalogue',
    },
  ];

  // ── stage SLA target map for pipeline section ──
  const stageSLAMap: Record<string, string> = {};
  stageSLAs.forEach(s => { stageSLAMap[s.stageId] = s.targetDuration; });

  // ── channel priority colors ──
  const channelPriorityColor = (p: string) => {
    const map: Record<string, string> = {
      critical: 'bg-rose-500',
      high:     'bg-amber-500',
      medium:   'bg-blue-500',
      low:      'bg-gray-400',
    };
    return map[p] || 'bg-gray-400';
  };

  // ── quick nav sections ──
  const quickNav = [
    { icon: GitBranch,  label: 'Lead Pipeline',         labelAr: 'مسار العملاء',              href: '/pipeline',      color: 'bg-blue-500' },
    { icon: Clock,      label: 'SLA Catalogue',          labelAr: 'كتالوج مستوى الخدمة',       href: '/sla-catalogue', color: 'bg-amber-500' },
    { icon: Users,      label: 'Roles & Responsibilities', labelAr: 'الأدوار والمسؤوليات',    href: '/roles',         color: 'bg-violet-500' },
    { icon: BookOpen,   label: 'SOPs',                   labelAr: 'إجراءات التشغيل',           href: '/sops',          color: 'bg-cyan-500' },
    { icon: BarChart3,  label: 'Performance',            labelAr: 'الأداء',                    href: '/performance',   color: 'bg-emerald-500' },
    { icon: Layers,     label: 'Implementation',         labelAr: 'التنفيذ',                   href: '/implementation',color: 'bg-rose-500' },
    { icon: FileText,   label: 'Policies',               labelAr: 'السياسات',                  href: '/policies',      color: 'bg-indigo-500' },
    { icon: TrendingUp, label: 'CRM Configuration',      labelAr: 'إعدادات CRM',               href: '/crm',           color: 'bg-teal-500' },
  ];

  return (
    <div ref={printRef} className="space-y-10 animate-fade-in">

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isAr ? 'لوحة التحكم' : 'Dashboard'}
          </h1>
          <p className="mt-1 text-gray-500 text-sm">
            {isAr
              ? 'نظرة شاملة على نظام إدارة العملاء المحتملين — المجدوية موتورز'
              : 'System-wide view of the Almajdouie Motors Lead Management Operating System'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PrintButton
            onClick={handlePrint}
            isAr={isAr}
          />
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <span className="text-sm font-medium text-emerald-700">
              {isAr ? 'جاهز للتنفيذ' : 'Ready for Implementation'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <section>
        <SectionHeader
          icon={Activity}
          title="System Summary"
          titleAr="ملخص النظام"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {summaryCards.map(card => (
            <StatCard key={card.label} {...card} />
          ))}
        </div>
      </section>

      {/* ── Two-column: Pipeline + Channels ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Pipeline Stages */}
        <section>
          <SectionHeader
            icon={GitBranch}
            title="Pipeline Stages"
            titleAr="مراحل المسار"
            href="/pipeline"
            linkLabel="View pipeline"
            linkLabelAr="عرض المسار"
          />
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Pre-stage */}
            {preStage && (
              <Link
                to={`/pipeline/stage/${preStage.meta.id}`}
                className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100 hover:bg-gray-100 transition-colors group"
              >
                <span className="w-7 h-7 rounded-lg bg-gray-400 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">0</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {isAr ? preStage.meta.titleAr : preStage.meta.title}
                  </p>
                  <p className="text-xs text-gray-400">{isAr ? preStage.meta.slaAr : preStage.meta.sla}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </Link>
            )}
            {/* CRM stages */}
            {crmStages.map((stage, idx) => (
              <Link
                key={stage.meta.id}
                to={`/pipeline/stage/${stage.meta.id}`}
                className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${idx < crmStages.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                <span
                  className="w-7 h-7 rounded-lg text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: stage.meta.color }}
                >
                  {stage.meta.order}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {isAr ? stage.meta.titleAr : stage.meta.title}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {isAr ? stage.meta.ownerAr : stage.meta.owner}
                    {stageSLAMap[stage.meta.id] && (
                      <span className="ml-2 rtl:mr-2 rtl:ml-0 text-amber-600">· {stageSLAMap[stage.meta.id]}</span>
                    )}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </Link>
            ))}
          </div>
        </section>

        {/* Right column: Channel SLAs + Outcomes */}
        <div className="space-y-8">

          {/* Channel SLAs */}
          <section>
            <SectionHeader
              icon={Zap}
              title="Channel SLAs"
              titleAr="مستويات خدمة القنوات"
              href="/sla-catalogue"
              linkLabel="View SLAs"
              linkLabelAr="عرض مستويات الخدمة"
            />
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {channelSLAs.map((sla, idx) => (
                <Link
                  key={sla.id}
                  to={`/sla-catalogue/channel/${sla.id}`}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${idx < channelSLAs.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${channelPriorityColor(sla.priority)}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {isAr ? sla.channelAr : sla.channel}
                    </p>
                    <p className="text-xs text-amber-600 font-medium">{sla.timeToFirstContact}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${priorityBadge(sla.priority)}`}>
                    {sla.priority}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Outcomes */}
          <section>
            <SectionHeader
              icon={Target}
              title="Pipeline Outcomes"
              titleAr="نتائج المسار"
              href="/pipeline"
            />
            <div className="grid grid-cols-2 gap-2">
              {outcomes.map(o => {
                const outcomeColors: Record<string, string> = {
                  won:         'bg-emerald-50 border-emerald-200 text-emerald-700',
                  lost:        'bg-rose-50 border-rose-200 text-rose-700',
                  nurture:     'bg-blue-50 border-blue-200 text-blue-700',
                  duplicate:   'bg-gray-50 border-gray-200 text-gray-600',
                  invalid:     'bg-orange-50 border-orange-200 text-orange-700',
                  unreachable: 'bg-amber-50 border-amber-200 text-amber-700',
                };
                const cls = outcomeColors[o.id] || 'bg-gray-50 border-gray-200 text-gray-600';
                return (
                  <div key={o.id} className={`rounded-xl border px-3 py-2.5 ${cls}`}>
                    <p className="text-sm font-semibold capitalize">{isAr ? o.titleAr : o.title}</p>
                    {o.requiresReasonCode && (
                      <p className="text-xs opacity-70 mt-0.5">{isAr ? 'يتطلب رمز السبب' : 'Requires reason code'}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      {/* ── Two-column: Roles + SOPs ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Role Groups */}
        <section>
          <SectionHeader
            icon={Users}
            title="Team & Role Coverage"
            titleAr="الفريق وتغطية الأدوار"
            href="/roles"
            linkLabel="View all roles"
            linkLabelAr="عرض جميع الأدوار"
          />
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {roleGroups.map((group, idx) => {
              const groupRoles = roles.filter(r => r.group === group.id);
              const gc = group.color || '#6b7280';
              return (
                <div
                  key={group.id}
                  className={`flex items-center gap-3 px-4 py-3 ${idx < roleGroups.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: gc + '20', border: `1px solid ${gc}40` }}
                  >
                    <span className="text-sm font-bold" style={{ color: gc }}>
                      {groupRoles.length > 0 ? groupRoles.length : '—'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">{isAr ? group.labelAr : group.label}</p>
                    {groupRoles.length > 0 && (
                      <p className="text-xs text-gray-400 truncate">
                        {groupRoles.map(r => isAr ? r.titleAr : r.title).join(' · ')}
                      </p>
                    )}
                    {groupRoles.length === 0 && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        {isAr ? 'لا توجد أدوار محددة' : 'No roles defined yet'}
                      </p>
                    )}
                  </div>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
                    style={{ backgroundColor: gc + '15', color: gc }}
                  >
                    {groupRoles.length} {isAr ? 'دور' : 'role'}{groupRoles.length !== 1 ? 's' : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* SOPs */}
        <section>
          <SectionHeader
            icon={BookOpen}
            title="SOPs Overview"
            titleAr="نظرة عامة على الإجراءات"
            href="/sops"
            linkLabel="View all SOPs"
            linkLabelAr="عرض جميع الإجراءات"
          />
          <div className="space-y-3">
            {/* Breakdown cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3">
                <p className="text-2xl font-bold text-cyan-900">{sopCounts.stageSOPs}</p>
                <p className="text-sm text-cyan-700 font-medium">{isAr ? 'إجراءات المراحل' : 'Stage SOPs'}</p>
                <p className="text-xs text-cyan-600 mt-0.5">{isAr ? 'تغطي كل مرحلة من مراحل CRM' : 'Covering every CRM stage'}</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                <p className="text-2xl font-bold text-violet-900">{sopCounts.scenarioSOPs}</p>
                <p className="text-sm text-violet-700 font-medium">{isAr ? 'إجراءات السيناريوهات' : 'Scenario SOPs'}</p>
                <p className="text-xs text-violet-600 mt-0.5">{isAr ? 'حالات خاصة وحوادث' : 'Edge cases & incidents'}</p>
              </div>
            </div>

            {/* Critical SOPs */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-100">
                <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {isAr ? 'الإجراءات الحرجة' : 'Critical SOPs'}
                </p>
              </div>
              {criticalSOPs.map((sop, idx) => (
                <Link
                  key={sop.meta.id}
                  to={`/sops/${sop.meta.id}`}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${idx < criticalSOPs.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0 mt-1.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {isAr ? sop.meta.titleAr : sop.meta.title}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{isAr ? sop.meta.shortDescAr : sop.meta.shortDesc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
                </Link>
              ))}
              {/* High priority SOPs */}
              {getSOPsByPriority('high').slice(0, 3).map((sop, idx) => (
                <Link
                  key={sop.meta.id}
                  to={`/sops/${sop.meta.id}`}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${idx < 2 ? 'border-b border-gray-100' : ''}`}
                >
                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0 mt-1.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {isAr ? sop.meta.titleAr : sop.meta.title}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{isAr ? sop.meta.shortDescAr : sop.meta.shortDesc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
                </Link>
              ))}
            </div>

            {/* Priority breakdown */}
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">
                {isAr ? 'توزيع الأولوية' : 'Priority Breakdown'}
              </p>
              <div className="space-y-2">
                {sopCounts.byPriority.map(({ priority, count }) => {
                  const pct = Math.round((count / sopCounts.totalSOPs) * 100);
                  const barColors: Record<string, string> = {
                    critical: 'bg-rose-500',
                    high: 'bg-amber-400',
                    medium: 'bg-blue-400',
                    low: 'bg-gray-300',
                  };
                  return (
                    <div key={priority} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-16 capitalize">{priority}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${barColors[priority] || 'bg-gray-400'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700 w-6 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── SLA Stage Grid ── */}
      <section>
        <SectionHeader
          icon={Clock}
          title="Stage SLA Targets"
          titleAr="أهداف مستوى خدمة المراحل"
          href="/sla-catalogue"
          linkLabel="Full SLA catalogue"
          linkLabelAr="كتالوج مستوى الخدمة الكامل"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {stageSLAs.map(sla => (
            <Link
              key={sla.id}
              to={`/sla-catalogue/stage/${sla.id}`}
              className="bg-white border border-gray-200 rounded-xl p-3.5 hover:border-amber-300 hover:shadow-sm transition-all group"
            >
              <p className="text-xs text-gray-500 mb-1.5 font-medium truncate">
                {isAr
                  ? crmStages.find(s => s.meta.id === sla.stageId)?.meta.titleAr || sla.stageId
                  : crmStages.find(s => s.meta.id === sla.stageId)?.meta.title || sla.stageId}
              </p>
              <p className="text-sm font-bold text-amber-700">{sla.targetDuration}</p>
              <p className="text-xs text-gray-400 mt-0.5">max {sla.maxDuration}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Quick Navigation ── */}
      <section>
        <SectionHeader
          icon={Layers}
          title="Quick Navigation"
          titleAr="التنقل السريع"
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickNav.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3.5 hover:border-gray-300 hover:shadow-sm transition-all group"
            >
              <div className={`${item.color} rounded-lg p-2 flex-shrink-0`}>
                <item.icon className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                {isAr ? item.labelAr : item.label}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-gray-400 ml-auto rtl:mr-auto rtl:ml-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>
      </section>

      {/* ── Footer note ── */}
      <div className="flex items-center gap-2 text-xs text-gray-400 pb-2">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
        <span>
          {isAr
            ? 'جميع البيانات مستمدة من ملفات Markdown الرئيسية — المصدر الوحيد للحقيقة'
            : 'All data is live-parsed from the Markdown source-of-truth files — no hard-coded values'}
        </span>
      </div>
    </div>
  );
};

export default Dashboard;
